import type { ReactNode } from 'react';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import MessageDecorator from '../message-decorator';
import useMessageInfo from '../use-message-info';
import ContentAnnouncement from './content-announcement';
import ContentAudio from './content-audio';
import ContentCall from './content-call';
import ContentContact from './content-contact';
import ContentEmoticon from './content-emoticon';
import ContentFile from './content-file';
import ContentImage from './content-image';
import ContentInvite from './content-invite';
import ContentNotice from './content-notice';
import ContentText from './content-text';
import ContentVideo from './content-video';

interface Props {
  type?: 'quote' | 'clamped';
  showHeader?: boolean;
  message: ConversationMessageTypeUnion;
  onResize?: () => void;
}

export default function MessageContent({ message, showHeader = true, type, onResize }: Props) {
  const { isNotification, isGroupAnnouncement } = useMessageInfo(message);

  if (isNotification && !isGroupAnnouncement) return <ContentAnnouncement message={message} />;

  let content: ReactNode;
  switch (message.MsgType) {
    case ConversationMessageContentType.Emoticon:
      content = (
        <ContentEmoticon
          key={message.ID}
          message={message}
        />
      );
      break;
    case ConversationMessageContentType.Image:
      content = (
        <ContentImage
          key={message.ID}
          message={message}
        />
      );
      break;
    case ConversationMessageContentType.Video:
      content = (
        <ContentVideo
          key={message.ID}
          message={message}
        />
      );
      break;
    case ConversationMessageContentType.Audio:
      content = (
        <ContentAudio
          key={message.ID}
          message={message}
        />
      );
      break;
    case ConversationMessageContentType.File:
      content = (
        <ContentFile
          key={message.ID}
          message={message}
        />
      );
      break;
    case ConversationMessageContentType.Contact:
      content = (
        <ContentContact
          key={message.ID}
          message={message}
        />
      );
      break;
    case ConversationMessageContentType.Call:
      content = (
        <ContentCall
          key={message.ID}
          message={message}
        />
      );
      break;
    case ConversationMessageContentType.Notice:
      content = (
        <ContentNotice
          key={message.ID}
          message={message}
        />
      );
      break;
    case ConversationMessageContentType.Invite:
      content = (
        <ContentInvite
          key={message.ID}
          message={message}
        />
      );
      break;
    default:
      content = (
        <ContentText
          key={message.ID}
          type={type}
          message={message}
        />
      );
  }

  return (
    <MessageDecorator
      showHeader={showHeader}
      message={message}
      onResize={onResize}>
      {content}
    </MessageDecorator>
  );
}
